import React from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {downloadFile, getShipmentDocumentList, getShipmentPermissions} from "../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import Loader from "../core/Loader";
import {DescriptionOutlined, FileCopyOutlined, GetAppOutlined} from '@material-ui/icons';
import withWidth, {WithWidthProps} from "@material-ui/core/withWidth";
import List from "@material-ui/core/List";
import {Accordion, AccordionDetails, AccordionSummary, Grid, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {SHIPMENT_PERMISSIONS} from "../../constants/ShipmentPermissionsConstants";

declare global {
    interface Window {
        executeAndroidDownload: (link) => void
    }
}

interface ITransportDocumentDTO {
    shipmentId: string
    docId: number
    docNo: string
    docType: string
    docName: string
    docLink: string
    docDescription: string
    docMimeType: string
    deleted: boolean

}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            "& .MuiExpansionPanelDetails-root": {
                padding: 5,
                backgroundColor: "#fafafa"
            },
            "& .MuiPaper-root": {
                height: " 100%",
            }
        },
        title: {
            display: "flex",
            alignItems: "center",
            "& .MuiSvgIcon-root": {
                marginRight: 10
            }
        },
        link: {
            display: "flex",
            alignItems: "center",
            "& a": {
                textDecoration: "underline",
                color: theme.palette.primary.main,
                fontSize: 15
            }
        },
        shipmentInfoContainer: {
            display: "flex",
            alignItems: "center",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
        }
    }),
);

const DocumentsExpansionPanel: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const [documentsVisible, setDocumentsVisible] = React.useState(false);

    React.useEffect(() => {

        getShipmentPermissions(uuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {

                setDocumentsVisible(response.includes(SHIPMENT_PERMISSIONS.READ_DOCUMENT))
            }
        })
    }, [enqueueSnackbar, t, uuid]);


    if (!documentsVisible) {
        return <></>;
    }

    return (
        <div className={classes.root}>

            <Accordion TransitionProps={{unmountOnExit: true}}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    aria-controls="documents-content"
                    id="documents-header"
                >
                    <div className={classes.title}>
                        <FileCopyOutlined/>
                        <Typography variant="button">{t("document.caption")}</Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails >
                    <DocumentsGrid uuid={uuid} width={width}/>
                </AccordionDetails >
            </Accordion>
        </div>
    );
};


const DocumentsGrid: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {

    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const classes = useStyles();

    const [shipmentDocuments, setShipmentDocuments] = React.useState<ITransportDocumentDTO[] | undefined>(undefined);


    React.useEffect(() => {

        getShipmentDocumentList(uuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentDocuments(response);
            }
        })
    }, [enqueueSnackbar, t, uuid]);

    if (!shipmentDocuments) {
        return <div className={classes.root}>
            <Loader/>
        </div>;
    }

    if (shipmentDocuments.length === 0) {
        return <div className={classes.root}>
            <Typography variant="subtitle2">{t("document.no-documents")}</Typography>
        </div>;
    }

    return <div className={classes.root}>
        <List>
            {shipmentDocuments?.filter(doc => !doc.deleted).map((doc, index) => <DocumentItem key={index}
                                                                                              doc={doc}
                                                                                              width={width}/>)}


        </List></div>
};

const DocumentItem: React.FC<{ doc: ITransportDocumentDTO } & WithWidthProps> = ({doc, width}) => {
    const classes = useStyles();


    return <ListItem button onClick={() => {

        downloadFile(doc).then((response) => {
            const {data} = response;

            const reader = new FileReader();
            reader.readAsDataURL(data);
            reader.onloadend = function () {
                const base64data = reader.result;

                const url = window.URL.createObjectURL(data);
                const link = document.createElement('a');

                if ((window.navigator as any).msSaveBlob) { // IE 10+
                    const blob = new Blob([data], { type: doc.docMimeType });
                    (window.navigator as any).msSaveBlob(blob, doc.docName);

                } else {

                    link.href = base64data ? base64data.toString() : url;
                    link.setAttribute('download', doc.docName);
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                }
            }

        })
    }
    }>
        <ListItemIcon>
            <DescriptionOutlined fontSize="large"/>
        </ListItemIcon>
        <ListItemText
            primary={<Grid container>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">{`${doc.docNo} - ${doc.docType}`}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <div className={classes.shipmentInfoContainer}
                         style={{width: width === "xs" ? '12rem' : '25rem'}}
                    >
                        <Typography variant="subtitle1">{doc.docName}</Typography>
                    </div>
                </Grid>
            </Grid>}
        />
        <ListItemSecondaryAction>
            <IconButton size="medium" color="primary">
                <GetAppOutlined/>
            </IconButton>
        </ListItemSecondaryAction>
    </ListItem>
};


export default withWidth()(DocumentsExpansionPanel);